import { Column } from "@/constants/pipeline";
import { ArrowDown, ArrowUp } from "lucide-react";
import * as React from "react";

type StaticPipelineCardProps = {
  title: string;
  columns: Column[];
  headerColumns?: Column[];
  children: React.ReactNode;
  classes?: {
    wrapper?: string;
    header?: string;
  };
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSort?: (sortKey: string) => void;
  useFixedLayout?: boolean;
};

export const StaticPipelineCard: React.FC<StaticPipelineCardProps> = ({
  title,
  columns,
  headerColumns,
  children,
  classes = {
    wrapper: "!bg-primary-50 border-primary-200",
    header: "",
  },
  sortBy,
  sortOrder,
  onSort,
  useFixedLayout = true,
}) => {
  return (
    <div className={`h-full rounded-[6px] border border-solid p-2 ${classes.wrapper}`}>
      {!headerColumns && (
        <div className="px-3 py-1.5">
          <span className="text-sm font-bold">{title}</span>
        </div>
      )}
      <div className="w-full overflow-x-auto overflow-y-hidden">
        <table className={`w-full border-collapse ${useFixedLayout ? "md:table-fixed" : ""}`}>
          {useFixedLayout && (
            <colgroup className="hidden md:table-column-group">
              {Array.from({ length: 11 }, (_, i) => (
                <col key={i} />
              ))}
            </colgroup>
          )}
          {headerColumns && (
            <thead>
              <tr className="h-10">
                {headerColumns.map((column, index) => {
                  const isTitle = index === 0 && !column.label;
                  const alignClass =
                    column.align === "center"
                      ? "text-center [&>*]:mx-auto"
                      : column.align === "right"
                        ? "text-right"
                        : "text-left";
                  const paddingClass = column.reducePadding ? "px-3 md:px-0 py-3" : "px-3 py-3";
                  const content = isTitle ? title : column.label;
                  const textSizeClass = isTitle ? "text-sm" : "text-xs";
                  return (
                    <th
                      key={column.key || index}
                      colSpan={column.colspan}
                      className={`${paddingClass} align-middle ${textSizeClass} font-bold whitespace-nowrap text-slate-700 ${alignClass} ${classes.header}`}
                    >
                      {content}
                    </th>
                  );
                })}
              </tr>
            </thead>
          )}
          <thead>
            <tr className="h-10">
              {columns.map((column, index) => {
                const alignClass =
                  column.align === "center"
                    ? "text-center [&>*]:mx-auto"
                    : column.align === "right"
                      ? "text-right"
                      : "text-left";
                const paddingClass = column.reducePadding ? "px-3 md:px-0 py-3" : "px-3 py-3";
                return (
                  <th
                    key={column.key || index}
                    colSpan={column.colspan}
                    className={`${paddingClass} align-middle text-xs font-bold whitespace-nowrap text-slate-700 ${alignClass}`}
                  >
                    {column.sortable !== false && column.key && onSort ? (
                      <div
                        onClick={() => onSort(column.key)}
                        className="relative w-fit cursor-pointer select-none hover:opacity-70"
                      >
                        {sortBy === column.key && sortOrder === "asc" && (
                          <ArrowUp
                            className={
                              "animate-in slide-in-from-bottom-1 absolute top-1/2 -right-3.5 h-3 w-3 -translate-y-1/2 text-slate-700 duration-500"
                            }
                          />
                        )}
                        {column.label}
                        {sortBy === column.key && sortOrder === "desc" && (
                          <ArrowDown
                            className={
                              "animate-in slide-in-from-top-1 absolute top-1/2 -right-3.5 h-3 w-3 -translate-y-1/2 text-slate-700 duration-500"
                            }
                          />
                        )}
                      </div>
                    ) : (
                      column.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="rounded-md border border-solid border-slate-200 bg-white">{children}</tbody>
        </table>
      </div>
    </div>
  );
};
